import { twMerge } from "tailwind-merge";

import { useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import Button from "../button";

export interface ChipItem {
  id: string;
  label: React.ReactNode;
  variant?: "primary" | "light";
  disabled?: boolean;
}

export interface ChipsProps {
  className?: string;

  limit?: number;
  size?: "sm" | "lg";
  chips: ChipItem[];
  enableAdd?: boolean;

  onChipAdd?: () => void;
  onChipClicked?: (chip: ChipItem) => void;
}

export default function Chips(props: Readonly<ChipsProps>) {
  const {
    className,
    limit = 10,
    size = "sm",
    chips,
    enableAdd = false,
    onChipAdd,
    onChipClicked,
  } = props;

  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);

  const hiddenCount = Math.max(0, chips.length - limit);
  const visibleChips = chips.slice(0, expanded ? chips.length : limit);

  return (
    <div className={twMerge("flex flex-wrap gap-2", className)}>
      {enableAdd && (
        <Button
          size="sm"
          className="rounded-sm"
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation();
            onChipAdd?.();
          }}
        >
          <Plus size="16" />
          {t("common.button.add", "Add")}
        </Button>
      )}
      {visibleChips.map((chip) => (
        <Button
          key={chip.id}
          size={size}
          variant={chip.variant ?? "light"}
          className="text-start break-words"
          disabled={chip.disabled}
          onClick={(e) => {
            e.stopPropagation();
            onChipClicked?.(chip);
          }}
        >
          {chip.label}
        </Button>
      ))}
      {!visibleChips.length && (
        <Button size="sm" variant="light" disabled>
          {t("common.button.no_items", "No Items")}
        </Button>
      )}
      {hiddenCount > 0 && (
        <Button
          size="sm"
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((state) => !state);
          }}
        >
          {expanded
            ? t("common.button.show_less", "Show Less")
            : t("common.template.show_x_more", "Show {{value}} More", {
                value: hiddenCount,
              })}
        </Button>
      )}
    </div>
  );
}
